import React, { Component, ReactNode, RefObject } from "react";

import { duration } from "@formant/core/model/duration";
import { IDeviceConfigurationDocument } from "@formant/core/model/IDeviceConfigurationDocument";
import { rtcPointStreamType } from "@formant/core/model/rtcStreamTypes";
import { createRtcStreamMessage } from "@formant/rtc-client/utils/createRtcStreamMessage";

import { appStore, operateStore } from "../../stores";
import { ITeleopSession } from "../../teleop/ITeleopSession";
import ClickSignal, {
    ClickShape,
    ClickSignalFunctionReferenceMap,
} from "./ClickSignal";
import { getMappedCoords, rejectInputAnd } from "./TeleopPointMappers";
import { triggerOnCount } from "./triggerOnCount";

export interface ITeleopMouseInterceptorProps {
    children: ReactNode;
    session: ITeleopSession;
    document?: IDeviceConfigurationDocument;
    primary: boolean;
    target: string;
    boundsRef: RefObject<HTMLElement>;
}

const showDisabledInputWarning = () => {
    appStore.setSnackbar("Click event notification disabled in this mode.", {
        label: "Switch to Fit Image",
        onClick: () => {
            operateStore.fill = false;
        },
    });
};

const triggeredShowDisabledInputWarning = triggerOnCount(
    showDisabledInputWarning,
    3,
    1.25 * duration.second,
);

const rejectClick = rejectInputAnd(triggeredShowDisabledInputWarning);
const resetTrigger = () => triggeredShowDisabledInputWarning.reset();

export class TeleopMouseInterceptor extends Component<ITeleopMouseInterceptorProps> {
    private fRef: ClickSignalFunctionReferenceMap = ClickSignal.receiveClick(
        this.clickListener.bind(this),
    );

    clickListener(e: CustomEvent<ClickShape>) {
        this.onClick(e.detail);
    }

    onComponentWillUnmount() {
        const { name, listener } = this.fRef;
        ClickSignal.inst.removeEventListener(name, listener);
    }

    render() {
        const { children } = this.props;
        return (
            <div onClick={this.onClick} onDoubleClick={resetTrigger}>
                {children}
            </div>
        );
    }

    private onClick = (event: ClickShape) => {
        const { document, target, session, primary, boundsRef } = this.props;
        const { armed, contentDimensions, fill, shouldAutoFill } = operateStore;

        if (!boundsRef.current || !contentDimensions) {
            return;
        }

        const bounds = boundsRef.current.getBoundingClientRect();
        const isFilled = fill ?? shouldAutoFill;
        const mapFn = !isFilled ? getMappedCoords : rejectClick;
        const point = mapFn(event, bounds, contentDimensions);

        if (point === false || !document || !armed || !primary) {
            return;
        }

        const streamName =
            document.teleop?.rosStreams?.find(
                _ => _.mouseEventsTargetStream === target,
            )?.topicName ||
            document.teleop?.customStreams?.find(
                _ => _.mouseEventsTargetStream === target,
            )?.name;

        if (!streamName) {
            return;
        }

        const { deviceId } = session;
        const pointStream = {
            streamName,
            streamType: rtcPointStreamType,
            entityId: deviceId,
        };

        const message = createRtcStreamMessage(pointStream, { point });
        session.send(message, { channelLabel: "stream.latest-reliable" });
    };
}
